/**
 * Header: sucursal de la sesión + caja abierta + tasas vigentes (la tasa 2, solo la dueña) + usuario. Banner de contexto
 * superadmin. En el menú del usuario, «Instalar app» (aplicación instalable) y, debajo de la cabecera, el aviso de versión nueva.
 */
import type { TasaVigenteDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Bell, BellRing, BookOpen, ChevronDown, Download, LogOut, RefreshCw, ShieldAlert, Volume2, VolumeX, Wallet } from 'lucide-react';
import { BotonAyuda } from '@/ayuda/BotonAyuda.tsx';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/componentes/ui/dropdown-menu.tsx';
import { get } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { AVISOS_QUE_ESPERAN, AVISOS_SONOROS, guardarSonido, prepararSonido, sonarAviso, sonidoActivo, VIGENCIA_AVISO_MS } from '@/lib/avisos.ts';
import { instalarApp, mostrarInstalar, usePwa } from '@/lib/pwa.ts';
import { claves } from '@/lib/query.ts';
import { veTasa2 } from '@/lib/tasa2.ts';
import { ETIQUETA_ROL, formatearFechaHora, iniciales } from '@/lib/utils.ts';
import { useLeerNotificacion, useNotificaciones } from '@/modulos/traslados/api.ts';
import { SelectorSucursal } from './SelectorSucursal.tsx';

/** Campana de notificaciones internas (traslados en tránsito, discrepancias): sin push, se refresca cada 15 segundos. */
function Notificaciones() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const notif = useNotificaciones(!!usuario);
  const leer = useLeerNotificacion();
  const [sonido, setSonido] = useState(sonidoActivo);
  const n = notif.data?.no_leidas ?? 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative rounded-lg p-2 hover:bg-accent" aria-label={`Notificaciones (${n} sin leer)`} data-testid="notif-bell">
          <Bell className="h-4 w-4" />
          {n > 0 ? <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-mg-rojo px-1 text-[10px] font-bold text-white" data-testid="notif-count">{n}</span> : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          Notificaciones
          <span className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1 text-xs font-normal underline" onClick={() => { guardarSonido(!sonido); setSonido(!sonido); }} title="Los avisos que esperan por ti suenan al llegar" data-testid="notif-sonido" data-activo={sonido ? 'si' : 'no'}>
              {sonido ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />} {sonido ? 'con sonido' : 'en silencio'}
            </button>
            {n > 0 ? <button className="text-xs font-normal underline" onClick={() => leer.mutate(null)}>marcar todas leídas</button> : null}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(notif.data?.datos ?? []).length === 0 ? <p className="px-2 py-3 text-xs text-muted-foreground">Sin notificaciones.</p> : null}
        {(notif.data?.datos ?? []).slice(0, 10).map((x) => (
          <DropdownMenuItem key={x.id} className={`flex flex-col items-start gap-0.5 ${x.leida_en ? 'opacity-60' : ''}`} onSelect={() => { if (!x.leida_en) leer.mutate(x.id); if (x.ruta) navigate(x.ruta); }} data-testid={`notif-item-${x.tipo}`}>
            <span className="text-sm font-medium">{x.titulo}</span>
            {x.cuerpo ? <span className="text-xs text-muted-foreground">{x.cuerpo}</span> : null}
            <span className="text-[10px] text-muted-foreground">{formatearFechaHora(x.creado_en)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
import { formatearTasa } from '@mg/shared';

/**
 * Avisos que esperan una acción de quien los recibe (autorización pedida, retiro por confirmar, talonario por
 * agotarse, efectivo sobre el límite): saltan en una franja bajo la cabecera, en cualquier pantalla, y suenan al
 * llegar. Se apagan al atenderlos (el servidor los marca leídos cuando la solicitud se resuelve) o con «Entendido».
 */
function AvisosQueEsperan() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const notif = useNotificaciones(!!usuario);
  const leer = useLeerNotificacion();
  const sinLeer = (notif.data?.datos ?? []).filter((x) => !x.leida_en && notif.dataUpdatedAt - new Date(x.creado_en).getTime() < VIGENCIA_AVISO_MS);
  const esperan = sinLeer.filter((x) => AVISOS_QUE_ESPERAN.has(x.tipo));
  const firma = sinLeer.filter((x) => AVISOS_SONOROS.has(x.tipo)).map((x) => x.id).join(',');
  const oidos = useRef<Set<string> | null>(null);
  const cargado = !!notif.data;
  useEffect(() => prepararSonido(), []);
  useEffect(() => {
    if (!cargado) return;
    const ids = firma ? firma.split(',') : [];
    // Lo que ya estaba al abrir la aplicación no suena: suena lo que llega después
    const nuevos = oidos.current ? ids.filter((id) => !oidos.current!.has(id)) : [];
    oidos.current = new Set([...(oidos.current ?? []), ...ids]);
    if (nuevos.length > 0) sonarAviso();
  }, [firma, cargado]);
  if (esperan.length === 0) return null;
  return (
    <div className="border-t border-red-300 bg-red-50 px-5 py-1.5 text-sm text-red-950" role="alert" data-testid="avisos-esperan">
      <ul className="space-y-1">
        {esperan.slice(0, 3).map((x) => (
          <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`aviso-espera-${x.tipo}`}>
            <BellRing className="h-4 w-4 shrink-0 text-mg-rojo" />
            <span className="min-w-0 flex-1"><b>{x.titulo}</b>{x.cuerpo ? <span className="ml-2 text-xs text-red-900/80">{x.cuerpo}</span> : null}</span>
            {x.ruta ? <Button size="sm" className="h-7" onClick={() => { leer.mutate(x.id); navigate(x.ruta!); }} data-testid="aviso-espera-ver">Ver</Button> : null}
            <Button size="sm" variant="outline" className="h-7" onClick={() => leer.mutate(x.id)} data-testid="aviso-espera-entendido">Entendido</Button>
          </li>
        ))}
      </ul>
      {esperan.length > 3 ? <p className="mt-1 text-xs">Y {esperan.length - 3} más en la campana.</p> : null}
    </div>
  );
}

/** El servidor ya tiene otra versión del sistema: se toma al recargar (nunca recarga sola: puede haber un cobro abierto). */
function AvisoVersionNueva() {
  const { versionNueva } = usePwa();
  if (!versionNueva) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t bg-mg-verde-claro px-5 py-1.5 text-sm text-mg-verde-oscuro" role="status" data-testid="aviso-version-nueva">
      <RefreshCw className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1">Hay una versión nueva: se aplicará al recargar.</span>
      <Button size="sm" variant="outline" className="h-7" onClick={() => window.location.reload()} data-testid="aviso-version-recargar">Recargar</Button>
    </div>
  );
}

/** iPhone y iPad: Safari no ofrece la ventana de instalar; se agrega a la pantalla de inicio desde Compartir. */
function DialogoInstalarIos({ abierto, cerrar }: { abierto: boolean; cerrar: () => void }) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) cerrar(); }}>
      <DialogContent data-testid="dialogo-instalar-ios">
        <DialogHeader>
          <DialogTitle>Instalar MG ERP en el iPhone o iPad</DialogTitle>
          <DialogDescription>En Safari: Compartir → Agregar a pantalla de inicio.</DialogDescription>
        </DialogHeader>
        <ol className="list-decimal space-y-1 pl-5 text-sm">
          <li>Abre esta página en <b>Safari</b>.</li>
          <li>Toca <b>Compartir</b> (el cuadro con la flecha hacia arriba).</li>
          <li>Elige <b>Agregar a pantalla de inicio</b> y toca <b>Agregar</b>.</li>
        </ol>
        <p className="text-xs text-muted-foreground">MG ERP queda en la pantalla de inicio y se abre como una aplicación. Necesita internet: sin conexión no se vende ni se cobra.</p>
        <DialogFooter>
          <Button onClick={cerrar} data-testid="dialogo-instalar-ios-entendido">Entendido</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Header() {
  const { usuario, cerrarSesion, salirContexto } = useAuth();
  const navigate = useNavigate();
  // Se relee cada medio minuto: si la dueña registra otra tasa, las cajas abiertas la ven sin recargar (H-03)
  const tasa = useQuery({ queryKey: claves.tasaVigente, queryFn: () => get<TasaVigenteDTO>('/tasas/vigente'), enabled: !!usuario, refetchInterval: 30_000 });
  const pwa = usePwa();
  const [instruccionesIos, setInstruccionesIos] = useState(false);
  if (!usuario) return null;

  const tasaVieja = tasa.data?.paralela_vieja ?? false;

  return (
    <header className="no-print sticky top-0 z-20 border-b bg-card" data-testid="header">
      {usuario.contexto_superadmin ? (
        <div className="flex items-center justify-between gap-3 bg-mg-ambar px-4 py-1.5 text-xs font-medium text-white" role="status" data-testid="banner-contexto">
          <span className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" /> Operando {usuario.sucursal_nombre} como superadmin — toda acción queda auditada
          </span>
          <Button size="sm" variant="secondary" className="h-7" onClick={() => void salirContexto().then(() => navigate('/consolidado'))}>
            Salir del contexto
          </Button>
        </div>
      ) : null}
      <div className="flex h-14 items-center gap-4 px-5">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold" data-testid="header-sucursal">
            {usuario.sucursal_nombre ?? 'Consola consolidada'}
          </p>
          <p className="truncate text-[11px] text-muted-foreground">
            {usuario.sesion_caja ? (
              <span className="inline-flex items-center gap-1" data-testid="header-caja">
                <Wallet className="h-3 w-3" /> {usuario.sesion_caja.caja_nombre} abierta desde {formatearFechaHora(usuario.sesion_caja.abierta_en, { day: undefined, month: undefined, year: undefined })}
              </span>
            ) : usuario.caja_en_otra_sucursal ? (
              // Caja que quedó abierta en otra sucursal: aquí no se opera, pero hay que saber que sigue abierta
              <span className="inline-flex items-center gap-1 text-mg-ambar" data-testid="header-caja-otra">
                <Wallet className="h-3 w-3" /> {usuario.caja_en_otra_sucursal.caja_nombre} de {usuario.caja_en_otra_sucursal.sucursal_nombre} abierta
              </span>
            ) : usuario.rol === 'cajero' ? (
              <span className="text-mg-ambar" data-testid="header-caja">Sin caja abierta</span>
            ) : (
              ETIQUETA_ROL[usuario.rol]
            )}
          </p>
        </div>
        {usuario.rol === 'superadmin' ? <SelectorSucursal /> : null}
        <div className="ml-auto flex items-center gap-2 text-xs" data-testid="header-tasas">
          {tasa.data ? (
            <>
              <Badge variant="neutro">BCV {formatearTasa(tasa.data.bcv)}</Badge>
              {/* El recordatorio es de la tasa 2 (la que escribe la dueña): se marca en su recuadro. Solo la ve la
                  dueña; los demás roles ven la BCV (pedido de la dueña 2026-10-19) */}
              {veTasa2(usuario) ? (
                <Badge variant={tasaVieja ? 'pendiente' : 'neutro'} title={`Tasa 2 escrita el ${formatearFechaHora(tasa.data.paralela_desde)}${tasaVieja ? ' · hay que actualizarla' : ''}`} data-testid="header-tasa-2" data-vieja={tasaVieja ? 'si' : 'no'}>
                  Paralela {formatearTasa(tasa.data.paralela)}
                </Badge>
              ) : null}
            </>
          ) : null}
        </div>
        <BotonAyuda />
        <Notificaciones />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-accent" data-testid="menu-usuario">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-mg-verde text-xs font-bold text-white">{iniciales(usuario.nombre)}</span>
              <span className="hidden text-left leading-tight sm:block">
                <span className="block text-sm font-medium">{usuario.nombre}</span>
                <span className="block text-[11px] text-muted-foreground">{ETIQUETA_ROL[usuario.rol]}</span>
              </span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{usuario.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/ayuda')} data-testid="menu-ayuda">
              <BookOpen /> Ayuda y manual
            </DropdownMenuItem>
            {/* Solo si el navegador lo ofrece (Chrome, Edge, Android) o en iPhone/iPad; no sale si ya está instalada */}
            {mostrarInstalar(pwa) ? (
              <DropdownMenuItem onSelect={() => { if (pwa.puedeInstalar) void instalarApp(); else setInstruccionesIos(true); }} data-testid="menu-instalar">
                <Download /> Instalar app
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={() => void cerrarSesion().then(() => navigate('/login'))} data-testid="cerrar-sesion">
              <LogOut /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <AvisosQueEsperan />
      <AvisoVersionNueva />
      <DialogoInstalarIos abierto={instruccionesIos} cerrar={() => setInstruccionesIos(false)} />
    </header>
  );
}
